import { HttpErrorResponse } from '@angular/common/http';
import { Component, OnInit, inject, signal } from '@angular/core';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { ToastModule } from 'primeng/toast';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { LocalizationService } from '../../core/localization/localization.service';
import { CustomerSelectorComponent } from '../../shared/design-system/customer-selector.component';
import { CurrencySelectorComponent } from '../../shared/design-system/currency-selector.component';
import { AinAmountPipe } from '../../shared/design-system/ain-amount.pipe';
import { AinPageComponent } from '../../shared/design-system/page.component';
import { AinStateComponent } from '../../shared/design-system/state.component';
import { SalesAiCopilotPanelComponent } from './sales-ai-copilot-panel.component';
import { REGULATED_PANEL_STYLES } from './sales-regulated-panel.styles';
import { RegulatedGoldOrderDetail, RegulatedGoldQuoteListItem, SalesRegulatedApiService } from './sales-regulated-api.service';

type GoldTab = 'overview' | 'locks' | 'assays' | 'actions';

interface SelectOption<T = string> {
  value: T;
  labelKey: string;
}

interface GoldQuoteSelectOption {
  value: string;
  label: string;
}

@Component({
  standalone: true,
  selector: 'ain-sales-gold-order-detail',
  providers: [MessageService],
  imports: [
    AinPageComponent, AinStateComponent, RouterLink, FormsModule, ButtonModule, ToastModule,
    InputTextModule, InputNumberModule, SelectModule, TableModule,
    CustomerSelectorComponent, CurrencySelectorComponent, AinAmountPipe, SalesAiCopilotPanelComponent
  ],
  template: `
    <p-toast />
    <ain-page [title]="isCreate() ? t('sales.regulated.gold.newOrder') : t('sales.regulated.gold.detailTitle')" [description]="t('sales.regulated.gold.description')">
      @if (loading()) { <ain-state type="loading" [title]="t('common.loading')" /> } @else {
        <div class="layout">
          <section class="main">
            <header class="card"><a class="back-link" [routerLink]="['/sales/gold-orders']">{{ t('catalog.action.backToList') }}</a></header>
            @if (isCreate()) {
              <p class="hint card">{{ t('sales.regulated.gold.suitabilityHint') }}</p>
              @if (saveError()) {
                <div class="form-error card">
                  <span>{{ saveError() }}</span>
                  @if (showSuitabilityAction()) {
                    <button pButton type="button" severity="secondary" [label]="t('sales.regulated.gold.recordSuitability')" (click)="goRecordSuitability()"></button>
                  }
                </div>
              }
              <section class="card grid">
                <label><span>{{ t('sales.regulated.gold.fields.side') }}</span>
                  <p-select [options]="sides" optionValue="value" [(ngModel)]="form.side" appendTo="body">
                    <ng-template pTemplate="selectedItem" let-item>{{ item ? t(item.labelKey) : '-' }}</ng-template>
                    <ng-template pTemplate="item" let-item>{{ t(item.labelKey) }}</ng-template>
                  </p-select>
                </label>
                <label><span>{{ t('sales.regulated.gold.fields.quantity') }}</span>
                  <p-inputNumber [(ngModel)]="form.quantityGrams" [min]="0" mode="decimal" [minFractionDigits]="0" [maxFractionDigits]="4" [useGrouping]="false" />
                </label>
                <label><span>{{ t('sales.regulated.gold.fields.limitPrice') }}</span><p-inputNumber [(ngModel)]="form.limitPrice" [min]="0" /></label>
                <label><span>{{ t('sales.regulated.gold.fields.quote') }}</span>
                  <p-select [options]="quoteOptions()" optionLabel="label" optionValue="value" [(ngModel)]="form.quoteId" [showClear]="true" [filter]="true" filterBy="label" appendTo="body" />
                </label>
                <label class="full"><span>{{ t('sales.orders.fields.customer') }}</span><ain-customer-selector [(ngModel)]="form.customerAccountId" /></label>
                <button pButton type="button" [label]="t('common.save')" (click)="save()"></button>
              </section>
            } @else if (order(); as o) {
              <section class="summary card">
                <article><span>{{ t('sales.regulated.gold.fields.side') }}</span><strong>{{ o.side }}</strong></article>
                <article><span>{{ t('columns.status') }}</span><strong>{{ o.status }}</strong></article>
                <article><span>{{ t('sales.regulated.gold.fields.quantity') }}</span><strong>{{ o.quantityGrams }}g</strong></article>
                <article><span>{{ t('sales.regulated.gold.fields.limitPrice') }}</span><strong>{{ o.limitPrice | ainAmount }}</strong></article>
              </section>
              <nav class="tabs">
                @for (tab of tabs; track tab) {
                  <button type="button" [class.active]="activeTab() === tab" (click)="activeTab.set(tab)">{{ tabLabel(tab) }}</button>
                }
              </nav>
              @if (activeTab() === 'overview') {
                <p class="card">{{ t('sales.regulated.gold.fields.quoteId') }}: {{ o.quoteId ?? '-' }}</p>
              }
              @if (activeTab() === 'locks') {
                <p-table [value]="o.priceLocks">
                  <ng-template pTemplate="header"><tr><th>{{ t('sales.regulated.gold.fields.metal') }}</th><th>{{ t('sales.regulated.gold.fields.lockedPrice') }}</th><th>{{ t('columns.status') }}</th></tr></ng-template>
                  <ng-template pTemplate="body" let-row><tr><td>{{ row.metalCode }}</td><td>{{ row.lockedPrice | ainAmount }}</td><td>{{ row.isActive ? t('common.yes') : t('common.no') }}</td></tr></ng-template>
                </p-table>
              }
              @if (activeTab() === 'assays') {
                <p-table [value]="o.assays">
                  <ng-template pTemplate="header"><tr><th>{{ t('sales.regulated.gold.fields.purity') }}</th><th>{{ t('sales.regulated.gold.fields.weight') }}</th><th>{{ t('sales.regulated.gold.fields.certificate') }}</th></tr></ng-template>
                  <ng-template pTemplate="body" let-row><tr><td>{{ row.purityPercent }}%</td><td>{{ row.weightGrams }}</td><td>{{ row.certificateNumber }}</td></tr></ng-template>
                </p-table>
              }
              @if (activeTab() === 'actions') {
                <section class="card grid">
                  <label><span>{{ t('sales.regulated.gold.settleAmount') }}</span><p-inputNumber [(ngModel)]="settleAmount" [min]="0" /></label>
                  <label><span>{{ t('sales.installments.fields.currency') }}</span><ain-currency-selector [(ngModel)]="settleCurrency" /></label>
                  <button pButton type="button" [label]="t('sales.regulated.gold.settle')" (click)="settle()"></button>
                  <label><span>{{ t('sales.regulated.gold.fields.purity') }}</span><p-inputNumber [(ngModel)]="assayForm.purityPercent" [min]="0" [max]="100" /></label>
                  <label><span>{{ t('sales.regulated.gold.fields.weight') }}</span><p-inputNumber [(ngModel)]="assayForm.weightGrams" [min]="0" /></label>
                  <label><span>{{ t('sales.regulated.gold.fields.certificate') }}</span><input pInputText [(ngModel)]="assayForm.certificateNumber" /></label>
                  <button pButton type="button" severity="secondary" [label]="t('sales.regulated.gold.recordAssay')" (click)="recordAssay()"></button>
                </section>
              }
            }
          </section>
          @if (!isCreate() && order()) {
            <ain-sales-ai-copilot-panel entityType="PreciousMetalOrder" [entityId]="order()!.id" defaultRecommendationType="NextBestAction" />
          }
        </div>
      }
    </ain-page>
  `,
  styles: [REGULATED_PANEL_STYLES]
})
export class SalesGoldOrderDetailComponent implements OnInit {
  private readonly api = inject(SalesRegulatedApiService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly localization = inject(LocalizationService);
  private readonly messages = inject(MessageService);
  readonly loading = signal(true);
  readonly isCreate = signal(false);
  readonly order = signal<RegulatedGoldOrderDetail | null>(null);
  readonly activeTab = signal<GoldTab>('overview');
  readonly tabs: GoldTab[] = ['overview', 'locks', 'assays', 'actions'];
  readonly sides: SelectOption[] = [
    { value: 'Buy', labelKey: 'sales.regulated.gold.side.buy' },
    { value: 'Sell', labelKey: 'sales.regulated.gold.side.sell' }
  ];
  readonly quoteOptions = signal<GoldQuoteSelectOption[]>([]);
  readonly saveError = signal('');
  readonly showSuitabilityAction = signal(false);
  form = { side: 'Buy', quantityGrams: 1, limitPrice: 0, quoteId: null as string | null, customerAccountId: null as string | null };
  settleAmount = 0;
  settleCurrency = 'IRR';
  assayForm = { purityPercent: 99.5, weightGrams: 1, certificateNumber: '' };
  ngOnInit(): void {
    const id = this.route.snapshot.paramMap.get('orderId') ?? '';
    const quoteId = this.route.snapshot.queryParamMap.get('quoteId');
    this.isCreate.set(!id || id === 'new');
    if (quoteId) this.form.quoteId = quoteId;
    if (this.isCreate()) {
      this.loadQuoteOptions();
      this.loading.set(false);
      return;
    }
    this.reload(id);
  }
  loadQuoteOptions(): void {
    this.api.listGoldQuotes().subscribe({
      next: quotes => this.quoteOptions.set(quotes.map(q => ({ value: q.id, label: this.formatQuoteLabel(q) }))),
      error: () => this.quoteOptions.set([])
    });
  }
  formatQuoteLabel(q: RegulatedGoldQuoteListItem): string {
    return `${q.metalCode} · ${q.bidPrice} / ${q.askPrice} ${q.currencyCode}`;
  }
  reload(id: string): void {
    this.api.getGoldOrder(id).subscribe({
      next: o => { this.order.set(o); this.loading.set(false); },
      error: () => this.loading.set(false)
    });
  }
  save(): void {
    if (!this.form.customerAccountId) {
      this.saveError.set(this.t('sales.regulated.errors.customer_required'));
      this.showSuitabilityAction.set(false);
      return;
    }
    this.saveError.set('');
    this.showSuitabilityAction.set(false);
    const quantityGrams = Math.round(this.form.quantityGrams * 10_000) / 10_000;
    this.api.submitGoldOrder({ ...this.form, quantityGrams }).subscribe({
      next: o => void this.router.navigate(['/sales/gold-orders', o.id]),
      error: err => this.handleSaveError(err)
    });
  }
  handleSaveError(err: unknown): void {
    const code = this.regulatedErrorCode(err);
    const key = code ? `sales.regulated.errors.${code}` : 'sales.regulated.errors.save_failed';
    const translated = this.t(key);
    const message = translated !== key ? translated : this.t('sales.regulated.errors.save_failed');
    this.saveError.set(message);
    this.showSuitabilityAction.set(code === 'suitability_not_approved');
    this.messages.add({ severity: 'error', summary: message, life: 6000 });
  }
  regulatedErrorCode(err: unknown): string | null {
    if (!(err instanceof HttpErrorResponse)) return null;
    const body = err.error as { error?: string; Error?: string } | null;
    return body?.error ?? body?.Error ?? null;
  }
  goRecordSuitability(): void {
    const customerId = this.form.customerAccountId;
    if (!customerId) return;
    void this.router.navigate(['/sales/compliance-cases/suitability'], {
      queryParams: {
        customerAccountId: customerId,
        productCategory: 'Gold',
        returnUrl: '/sales/gold-orders/new',
        quoteId: this.form.quoteId ?? undefined
      }
    });
  }
  settle(): void {
    const id = this.route.snapshot.paramMap.get('orderId')!;
    if (!confirm(this.t('sales.regulated.confirm.settle'))) return;
    this.api.settleGoldOrder(id, this.settleAmount, this.settleCurrency).subscribe({
      next: () => this.reload(id)
    });
  }
  recordAssay(): void {
    const id = this.route.snapshot.paramMap.get('orderId')!;
    this.api.recordGoldAssay(id, this.assayForm).subscribe({ next: () => this.reload(id) });
  }
  tabLabel(tab: GoldTab): string {
    const map: Record<GoldTab, string> = {
      overview: 'sales.regulated.tabs.overview',
      locks: 'sales.regulated.gold.tabs.priceLocks',
      assays: 'sales.regulated.gold.tabs.assays',
      actions: 'sales.regulated.tabs.actions'
    };
    return this.t(map[tab]);
  }
  t(key: string): string { return this.localization.translate(key); }
}
